/**
 * nco-pdp-variants.js — Run 5.2 / T5 selector update
 *
 * Variant state manager for the PDP. Manages colour swatch selection, mount
 * type, finish, size, tone mode, second colour, tray colour, overflow, and
 * tap-hole selectors. Emits `nco:variant:change` events consumed by the
 * gallery (5.1) and other PDP components.
 *
 * On colour selection:
 *   1. Gallery image swap (via nco:variant:change → 5.1)
 *   2. Price update
 *   3. Availability + lead-time update
 *   4. Colour-specific content load from ColourContent metaobject
 *   5. Quick-fact chip refresh
 *   6. Shopify variant ID sync for add-to-cart
 *
 * Loaded as an ES module via:
 *   <script src="{{ 'nco-pdp-variants.js' | asset_url }}" type="module" defer></script>
 *
 * Standards: S2 (vanilla JS, no jQuery, event delegation), S5 (ARIA updates),
 *            S6 (deferred load).
 */

/* ------------------------------------------------------------------ */
/* Selectors                                                           */
/* ------------------------------------------------------------------ */

const SEL = {
  root: '[data-nco-pdp-variants]',
  variantData: '#nco-variant-data',
  colourContent: '#nco-colour-content',
  specSheetMap: '#nco-spec-sheet-map',
  specRegistry: '#nco-spec-registry',
  installGuideMap: '#nco-install-guide-map',
  variantIndex: '#nco-variant-index',
  colourImageMap: '#nco-colour-image-map',
  configurationImageMap: '#nco-configuration-image-map',
  configurationOverflowImageMap1: '#nco-configuration-overflow-image-map-1',
  configurationOverflowImageMap2: '#nco-configuration-overflow-image-map-2',
  colourHexMap: '#nco-colour-hex-map',
  hvMeta: '#nco-hv-meta',
  swatchHexTarget: '[data-nco-hexkey]',
  installGuideItem: '[data-nco-install-guide]',
  colourSwatch: '[data-nco-colour-swatch]',
  colourLabel: '[data-nco-colour-label]',
  alchemyOption: '[data-nco-alchemy-option]',
  mountOption: '[data-nco-mount-option]',
  finishOption: '[data-nco-finish-option]',
  sizeOption: '[data-nco-size-option]',
  toneOption: '[data-nco-tone-option]',
  secondaryColourOption: '[data-nco-secondary-colour-option]',
  secondaryColourLabel: '[data-nco-secondary-colour-label]',
  secondaryColourGroup: '[data-nco-variant-group="secondaryColour"]',
  trayColourOption: '[data-nco-tray-colour-option]',
  trayColourLabel: '[data-nco-tray-colour-label]',
  overflowOption: '[data-nco-overflow-option]',
  overflowFinishOption: '[data-nco-overflow-finish-option]',
  overflowFinishGroup: '[data-nco-variant-group="overflowFinish"]',
  overflowFinishLabel: '[data-nco-overflow-finish-label]',
  tapholeOption: '[data-nco-taphole-option]',
  addToCartBtn: '[data-nco-add-to-cart], form[data-product-form] button[type="submit"][name="add"], [data-add-to-cart]',
  unavailableMsg: '[data-nco-unavailable-msg]',
  productTitle: '[data-nco-product-title]',
  variantOptionTitle: '[data-nco-variant-option-title]',
  productDescription: '[data-nco-product-description]',
  specSheetLink: '[data-nco-spec-sheet-btn], [data-nco-spec-sheet-link]',
  specSheetListItem: '[data-nco-spec-sheet-list-item]',
  specSheetUnavailable: '[data-nco-spec-sheet-unavailable]',
  pricePrefix: '[data-nco-price-prefix]',
  priceValue: '[data-nco-price-value]',
  priceCompare: '[data-nco-price-compare]',
  availabilityStatus: '[data-nco-availability-status]',
  availabilityLead: '[data-nco-availability-lead]',
  colourContentDisplay: '[data-nco-colour-content-display]',
  sku: '.product-sku',
  variantIdInput: '[data-nco-variant-id]',
  chips: '[data-nco-pdp-chips]',
  chipValue: '[data-chip-value]',
};

const CSS = {
  swatchActive: 'nco-pdp-variants__swatch--active',
  optionActive: 'nco-pdp-variants__option-btn--active',
};

/* ------------------------------------------------------------------ */
/* State                                                               */
/* ------------------------------------------------------------------ */

let rootEl = null;
let variantStore = null;
let colourContentStore = null;
let specSheetMap = null;
/* Spec sheet resolution decoupled from the 250-variant + ~20 metaobject bracket caps:
   specSheetMap maps variantId -> registry handle (+ a "default" handle); specRegistry maps
   registry handle -> spec-sheet URL (built from product_colours-style .values, uncapped). */
let specRegistry = null;
/* True only during the first synchronous init() pass. While set, we seed the form's
   variant id / price / SKU from the default variant but DO NOT swap the gallery image:
   the page must stay on the server-rendered hero carousel image on load. The first real
   user selection clears this and resumes normal image swapping. */
let initialLoad = true;
let invalidExplicitVariant = false;
/* Complete colour→hex map ({ "<handle>": "#rrggbb" }) for ALL 29 colours + 4 overflow
   finishes, rendered server-side from product_colours.values (uncapped). Single source
   used to paint every dot: colour swatches, tray swatches, overflow-finish swatches.
   Replaces the capped `shop.metaobjects.product_colours[handle]` Liquid bracket lookup. */
let colourHexMap = null;
let productTitleFallback = '';
let productDescriptionFallback = '';
/* High-variant (>250) resolution data — present only when custom.variant_index exists.
   For cylinders/shelf/loop, Liquid's 250-variant cap means the array above is incomplete,
   so resolution comes from this server-rendered index instead. */
let hvIndex = null;        /* { "<opt1>|<opt2>|...": {id, price, sku} } */
let hvColourImg = null;    /* { "<colourHandle>": {src, alt} } */
/* Market-aware price resolution for high-variant products.
   custom.variant_index stores the Admin BASE price (shop currency), so formatting it
   directly showed e.g. $3,300 to a US visitor who is charged $2,890 — the EU/US/INTL
   price lists are independent books, not FX conversions of the AUD price.
   Liquid's variant.price / priceDisplay ARE presentment-market amounts, so we join the
   index to the (capped at 250) Liquid variant array by id and learn base -> market.
     hvMarketById   exact per-variant market price for the first 250 variants
     hvMarketByBase validated base-cents -> market band, for variants beyond the cap
   Bands are only trusted when a base price maps to exactly ONE market price. Shopify
   permits per-variant fixed prices, so the invariant is checked rather than assumed —
   but note the check can only see the variants Liquid emitted (<=250). A collision that
   exists ONLY among uncapped variants cannot be detected here, so the invariant is also
   enforced out-of-band by tools/qa/verify-market-price-bands.mjs, which walks every
   variant in every market and is a required gate after any price-book edit.
   Tier 3, for anything the first two cannot resolve: fetch sections/nco-variant-price.liquid
   via the Section Rendering API, which prices the exact variant server-side in the current
   market. Real collisions exist — shelf-02-basin and shelf-oval-basin carry per-variant AU
   fixed prices where one base price maps to two market prices — so this path is load-bearing,
   not theoretical. Until it resolves, the price reads "Price confirmed in cart" rather than
   a number that might be wrong. */
let hvMarketById = null;
let hvMarketByBase = null;
/* Shelf/Loop only: PDP-only per-configuration image URLs, from the separate
   custom.configuration_image_map metafield (NOT nested in colour_image_map, so
   collection cards / JSON-LD / GMC never see it). Shape:
   { version, images: { "<colourHandle>": { "<mount>-<NO|WO>-<0|1>": "<versioned cdn url>" } } }.
   The config key equals the resolved variant SKU segments 2-4. */
let hvConfigurationImages = null;
/* Shelf/Loop overflow-finish images (Batch 10): per-finish PDP renders keyed
   finishCodes[<CODE>][<colourHandle>]["<mount>-WO-<taphole>"]. Shard 1 carries the four
   existing finishes (OW/OB/OC/OBR) and IS emitted; shard 2 reserves the four pending codes
   (OBN/OCP/OLB/OSD) and is intentionally NOT emitted this release, so pending finishes stay
   absent from the storefront. Both optional: a missing/malformed shard leaves these null and
   resolution falls through to configuration_image_map (finish-agnostic) then colour-level. */
let hvOverflowImages1 = null;
let hvOverflowImages2 = null;
let hvOptionNames = null;  /* canonical option order, e.g. ["Colour","Mount","Configuration"] */
let hvConfigMap = null;    /* { "<tapHole>|<overflow>|<finish>": "<Configuration value>" } */
const state = {
  colour: '',
  metalFinish: '',
  concreteColour: '',
  mount: '',
  finish: '',
  sizeLabel: '',
  toneMode: '',
  secondaryColour: '',
  trayColour: '',
  overflow: '',
  overflowFinish: '',
  tapHole: '',
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function parseJSON(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (_) {
    return null;
  }
}

function findVariantById(id) {
  if (!variantStore || !variantStore.variants) return null;
  const wanted = Number(id);
  if (!Number.isFinite(wanted)) return null;
  for (let i = 0; i < variantStore.variants.length; i++) {
    if (Number(variantStore.variants[i].id) === wanted) return variantStore.variants[i];
  }
  return null;
}

function findHighVariantById(id) {
  if (!hvIndex || !hvOptionNames) return null;
  const wanted = Number(id);
  if (!Number.isFinite(wanted)) return null;
  for (const key in hvIndex) {
    const entry = hvIndex[key];
    if (!entry || Number(entry.id) !== wanted) continue;
    const parts = key.split('|');
    const mapped = {};
    for (let i = 0; i < hvOptionNames.length; i++) {
      const name = String(hvOptionNames[i] || '').toLowerCase();
      const value = parts[i] || '';
      if (name === 'colour' || name === 'color') mapped.colourName = value;
      else if (name === 'metal finish') mapped.metalFinish = value;
      else if (name === 'concrete colour' || name === 'concrete color' || name === 'concrete finish') mapped.concreteColour = value;
      else if (name === 'mount' || name === 'mount type') mapped.mount = value;
      else if (name === 'size') mapped.sizeLabel = value;
      else if (name === 'finish') mapped.finish = value;
      else if (name === 'tone' || name === 'tone mode') mapped.toneMode = value;
      else if (name === 'tray' || name === 'tray colour') mapped.trayColourName = value;
      else if (name === 'configuration') {
        const cfg = parseConfigValue(value);
        mapped.overflow = cfg.overflow;
        mapped.overflowFinish = cfg.overflowFinish;
        mapped.tapHole = cfg.tapHole;
      }
    }
    return Object.assign({ id: entry.id, sku: entry.sku || '', available: true }, mapped);
  }
  return null;
}

function seedStateFromVariant(variant) {
  if (!variant) return;
  if (variant.colourName) state.colour = variant.colourName;
  if (variant.metalFinish) state.metalFinish = variant.metalFinish;
  if (variant.concreteColour) state.concreteColour = variant.concreteColour;
  if (variant.mount) state.mount = variant.mount;
  if (variant.finish) state.finish = variant.finish;
  if (variant.sizeLabel) state.sizeLabel = variant.sizeLabel;
  if (variant.toneMode) state.toneMode = variant.toneMode;
  if (variant.secondaryColourName) state.secondaryColour = variant.secondaryColourName;
  if (variant.trayColourName) state.trayColour = variant.trayColourName;
  if (variant.overflow) state.overflow = variant.overflow;
  if (variant.overflowFinish) state.overflowFinish = variant.overflowFinish;
  if (variant.tapHole) state.tapHole = variant.tapHole;

  const optionNames = variantStore && variantStore.optionNames;
  if (Array.isArray(optionNames) && Array.isArray(variant.options)) {
    for (let i = 0; i < optionNames.length; i++) {
      const name = String(optionNames[i] || '').toLowerCase();
      const value = variant.options[i] || '';
      if (name === 'mount' || name === 'mount type') state.mount = value;
      else if (name === 'metal finish') state.metalFinish = value;
      else if (name === 'concrete colour' || name === 'concrete color' || name === 'concrete finish') state.concreteColour = value;
      else if (name === 'size') state.sizeLabel = value;
      else if (name === 'finish') state.finish = value;
      else if (name === 'tray' || name === 'tray colour') state.trayColour = value;
    }
  } else if (!state.mount && /\bwall\b/i.test(variant.title || '')) {
    state.mount = 'Wall';
  } else if (!state.mount && /\bsurface\b/i.test(variant.title || '')) {
    state.mount = 'Surface';
  }
}

/* ---- High-variant index resolution (>250 variants; no Storefront token) ---- */
function ncoHandleize(s) {
  return String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
function hvValueForOption(name) {
  const n = String(name || '').toLowerCase();
  if (n === 'colour' || n === 'color') return state.colour;
  if (n === 'metal finish') return state.metalFinish;
  if (n === 'concrete colour' || n === 'concrete color' || n === 'concrete finish') return state.concreteColour;
  if (n === 'mount') return state.mount;
  if (n === 'tray' || n === 'tray colour') return state.trayColour;
  if (n === 'finish') return state.finish;
  if (n === 'size') return state.sizeLabel;
  if (n === 'tone' || n === 'tone mode') return state.toneMode;
  if (n === 'configuration') {
    if (!hvConfigMap) return '';
    const prefix = state.tapHole + '|' + state.overflow + '|';
    const direct = hvConfigMap[prefix + state.overflowFinish];
    if (direct) return direct;
    /* OBR retained its SKU while the customer-facing finish name changed from Brass to
       Tiger Bronze. Accept either side of that controlled data migration so a cached URL,
       stale filter link, or partially-updated product cannot make the variant unresolvable. */
    if (state.overflowFinish === 'Brass') return hvConfigMap[prefix + 'Tiger Bronze'] || '';
    if (state.overflowFinish === 'Tiger Bronze') return hvConfigMap[prefix + 'Brass'] || '';
    return '';
  }
  return '';
}
/* Join custom.variant_index to the Liquid-emitted variant array by id. Populates the
   exact per-id market price and a base-cents -> market band map, dropping any band whose
   base price resolves to more than one market price. */
function buildHvMarketPrices() {
  hvMarketById = null;
  hvMarketByBase = null;
  if (!hvIndex || !variantStore || !Array.isArray(variantStore.variants)) return;
  const byId = new Map();
  for (let i = 0; i < variantStore.variants.length; i++) {
    const lv = variantStore.variants[i];
    const id = Number(lv.id);
    if (!Number.isFinite(id)) continue;
    byId.set(id, { price: Number(lv.price), display: lv.priceDisplay || '' });
  }
  if (!byId.size) return;
  const bands = new Map();
  const poisoned = new Set();
  for (const key in hvIndex) {
    const entry = hvIndex[key];
    const market = byId.get(Number(entry.id));
    if (!market || !Number.isFinite(market.price)) continue;
    const base = Math.round(parseFloat(entry.price) * 100);
    if (!Number.isFinite(base)) continue;
    const seen = bands.get(base);
    if (seen === undefined) {
      bands.set(base, market);
    } else if (seen.price !== market.price) {
      /* Same base price, two market prices: the band is ambiguous and unusable. */
      poisoned.add(base);
    }
  }
  for (const base of poisoned) bands.delete(base);
  hvMarketById = byId;
  hvMarketByBase = bands.size ? bands : null;
}

/* Resolve a market price for an index entry. Returns null when neither an exact id match
   nor a trusted band is available — callers must then leave the rendered price untouched. */
function hvMarketPrice(entry) {
  const exact = hvMarketById && hvMarketById.get(Number(entry.id));
  if (exact && Number.isFinite(exact.price)) return exact;
  const base = Math.round(parseFloat(entry.price) * 100);
  const band = hvMarketByBase && hvMarketByBase.get(base);
  if (band && Number.isFinite(band.price)) return band;
  return null;
}

/* ---- Tier 3: exact server-side price via the Section Rendering API ----
   Used only when neither the exact id join nor a trusted band can price a variant.
   Results are cached by variant id; a monotonically increasing token discards
   out-of-order responses so a slow early request can never overwrite a later selection. */
const hvPriceFetchCache = new Map();   /* variantId -> Promise<{price,display}|null> */
/* Selection epoch. Bumped by applyResolved() on EVERY selection, including invalid ones,
   so a slow tier-3 response for selection A can never paint over a later selection B that
   tiers 1-2 resolved synchronously (tier 3 would not have run for B, so a token bumped only
   on tier-3 starts would still match A). */
let hvSelectionEpoch = 0;

function hvProductHandleForFetch() {
  /* sections/merged-product-detail.liquid:52 already carries the canonical handle, which is
     more reliable than the URL: a PDP can be reached through /collections/x/products/y and
     through legacy handles that redirect. */
  const el = document.querySelector('[data-product-handle]');
  const attr = el && el.getAttribute('data-product-handle');
  if (attr) return attr;
  const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
  return m ? m[1] : null;
}

function hvFetchExactPrice(variantId) {
  /* Promise cache, so rapid A-B-A swatch clicking cannot issue duplicate in-flight requests. */
  if (hvPriceFetchCache.has(variantId)) return hvPriceFetchCache.get(variantId);
  const handle = hvProductHandleForFetch();
  if (!handle) return Promise.resolve(null);
  /* Must go through Shopify.routes.root: a hardcoded /products/... path drops the
     market/locale subfolder and can come back priced in the shop's default currency. */
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const base = root.endsWith('/') ? root : root + '/';
  const promise = (async () => {
    try {
      const res = await fetch(
        `${base}products/${encodeURIComponent(handle)}?variant=${encodeURIComponent(variantId)}`
        + `&section_id=nco-variant-price`,
        { headers: { 'X-Requested-With': 'XMLHttpRequest' } },
      );
      if (!res.ok) return null;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector('[data-nco-variant-price]');
      if (!node) return null;
      const data = JSON.parse(node.textContent);
      /* Shopify silently falls back to the first available variant for an unknown id, so
         only trust a payload that is actually the variant we asked for. */
      if (Number(data.id) !== Number(variantId)) return null;
      /* And only trust it if it came back in the market we are rendering. */
      const want = variantStore && variantStore.currency;
      if (want && data.currency !== want) return null;
      const out = { price: Number(data.price), display: data.priceDisplay || '' };
      if (!Number.isFinite(out.price) || !out.display) return null;
      return out;
    } catch (e) {
      return null;
    }
  })();
  hvPriceFetchCache.set(variantId, promise);
  /* Do not cache a failure forever — a transient 429 should be retryable. */
  promise.then((v) => { if (!v) hvPriceFetchCache.delete(variantId); });
  return promise;
}

/* Ask the server for the exact price, then paint it only if the selection has not moved on. */
function hvResolvePriceAsync(variantId) {
  const epoch = hvSelectionEpoch;
  hvFetchExactPrice(variantId).then((market) => {
    if (epoch !== hvSelectionEpoch) return;      /* a newer selection superseded this */
    const stillEl = document.querySelector('#product-price');
    if (!market) {
      /* Settle the transient "Updating price…" to a terminal, honest state. */
      if (stillEl && stillEl.getAttribute('data-nco-price-stale') === 'true') {
        stillEl.removeAttribute('aria-busy');
        stillEl.textContent = 'Price confirmed in cart';
      }
      return;
    }
    /* Belt and braces: the form must still be pointing at the variant we priced. */
    const idInput = document.querySelector('input[name="id"], select[name="id"]');
    if (idInput && Number(idInput.value) !== Number(variantId)) return;
    const priceEl = document.querySelector('#product-price');
    if (!priceEl) return;
    priceEl.removeAttribute('data-nco-price-stale');
    priceEl.removeAttribute('aria-busy');
    priceEl.textContent = market.display;
  });
}

/* Build the index key from the current selection (option order = hvOptionNames) and
   look it up. Returns a variant-shaped object, or null if the combo is incomplete or
   does not exist (the latter handles cylinders' partial colour x tray matrix). */
function resolveVariantFromIndex() {
  if (!hvIndex || !hvOptionNames) return null;
  const parts = [];
  for (let i = 0; i < hvOptionNames.length; i++) {
    const val = hvValueForOption(hvOptionNames[i]);
    if (!val) return null;
    parts.push(val);
  }
  const entry = hvIndex[parts.join('|')];
  if (!entry) return null;
  const img = (hvColourImg && state.colour) ? hvColourImg[ncoHandleize(state.colour)] : null;
  /* Never format entry.price directly — it is the shop-currency base amount. */
  /* A market entry without a display string is treated as a MISS, not formatted with the
     shop formatter — that is the very substitution this fix exists to remove. */
  const market = hvMarketPrice(entry);
  const usable = !!(market && market.display);
  return {
    id: entry.id,
    sku: entry.sku || '',
    price: usable ? market.price : null,
    priceDisplay: usable ? market.display : '',
    priceUnresolved: !usable,
    compareAtPriceDisplay: '',
    colourName: state.colour,
    available: true,
    _hvImage: img,
  };
}
/* For >250 products the capped #merged-variant-images map won't contain the resolved
   variant, so swap the main/thumb image directly from the per-colour image map. */
/* Build the mount-overflow-taphole config key from a resolved variant SKU
   (segments 2-4): "SH1-2-WO-1-BL" -> "2-WO-1", and the 6-seg with-finish
   "SH1-2-WO-1-BL-OW" -> "2-WO-1" too. The Batch 10 finish shard combines this
   key with the final SKU segment to resolve the exact overflow-finish render. */
function hvConfigKeyFromSku(sku) {
  const p = String(sku || '').split('-');
  return p.length >= 4 ? p.slice(1, 4).join('-') : '';
}
/* The overflow-finish CODE is the resolved variant SKU's final segment (e.g. OW/OB/OC/OBR),
   derived from the SKU itself, not a human finish name. Returns '' for no-overflow SKUs
   (whose final segment is the colour code) and any non-finish token, so those fall through
   to the finish-agnostic configuration_image_map. */
var HV_OVERFLOW_FINISH_CODES = { OW: 1, OB: 1, OC: 1, OBR: 1, OBN: 1, OCP: 1, OLB: 1, OSD: 1 };
function hvFinishCodeFromSku(sku) {
  const p = String(sku || '').split('-');
  const last = p[p.length - 1];
  return HV_OVERFLOW_FINISH_CODES[last] ? last : '';
}
/* Append a Shopify CDN width transform, preserving any existing ?v= version param. */
/* Append a CDN width, IDEMPOTENTLY. An earlier version appended unconditionally,
   which is safe for config URLs (always bare) but not for colour_image_map URLs:
   the native collection map is emitted with width=800 already
   (nco-collection-filters.liquid), so a blind append yields ?width=800&width=800. */
function withWidth(url, w) {
  if (!url) return url;
  if (/[?&]width=/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + w);
  return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
}
/* Decompose a Configuration option value into the separate control states, mirroring
   the #nco-hv-meta configMap key derivation (tapHole Yes/No, overflow Overflow/No
   Overflow, finish = text after "Overflow - "). Used to reverse-resolve a deep
   ?variant= id (beyond Liquid's 250-variant cap) back into native option state. */
function parseConfigValue(cfg) {
  cfg = String(cfg || '');
  const tapHole = cfg.indexOf('1 Tap Hole') >= 0 ? 'Yes' : 'No';
  const overflow = cfg.indexOf('No Overflow') >= 0 ? 'No Overflow' : 'Overflow';
  let overflowFinish = '';
  const i = cfg.indexOf('Overflow - ');
  if (i >= 0) overflowFinish = cfg.slice(i + 'Overflow - '.length).trim();
  return { tapHole: tapHole, overflow: overflow, overflowFinish: overflowFinish };
}

function updateHvImage(variant) {
  if (!variant) return;
  const hv = variant._hvImage;   /* colour_image_map entry — may be null for a colour
                                    absent from colour_image_map; the config map still resolves */
  let src = '';
  let isConfig = false;
  let isOverflowFinish = false;
  /* 0. Shelf/Loop overflow-finish (Batch 10): exact per-FINISH URL from shard 1 (existing
        finishes) or shard 2 (pending — not emitted this release), keyed by the finish CODE
        from the SKU's final segment + colour handle + config key (SKU segments 2-4). Highest
        priority so a with-overflow variant shows its finish-specific render; falls through to
        the finish-agnostic configuration_image_map below when no shard/entry matches. */
  if (state.colour && variant.sku) {
    const fc = hvFinishCodeFromSku(variant.sku);
    const ck = hvConfigKeyFromSku(variant.sku);
    if (fc && ck) {
      const shard = (hvOverflowImages1 && hvOverflowImages1.finishCodes && hvOverflowImages1.finishCodes[fc])
                 || (hvOverflowImages2 && hvOverflowImages2.finishCodes && hvOverflowImages2.finishCodes[fc]);
      const byColour = shard && shard[ncoHandleize(state.colour)];
      if (byColour && byColour[ck]) {
        src = byColour[ck];
        isConfig = true;
        isOverflowFinish = true;
      }
    }
  }
  /* 1. Shelf/Loop: exact per-configuration URL from the separate configuration map,
        keyed by colour handle + the resolved variant's SKU config segments (2-4).
        Consulted BEFORE and independent of colour_image_map, so it works even when a
        colour has no colour-level src yet (the Loop colour-gap case). */
  if (!isConfig && hvConfigurationImages && hvConfigurationImages.images && state.colour && variant.sku) {
    const byColour = hvConfigurationImages.images[ncoHandleize(state.colour)];
    const ck = hvConfigKeyFromSku(variant.sku);
    if (byColour && ck && byColour[ck]) { src = byColour[ck]; isConfig = true; }
  }
  /* 2. Cylinder tray ladder (tray_srcs -> src_tray -> src), else colour-level src.
        Only when no config URL applied — config and tray products are disjoint, so a
        stale/crafted ?tray= on Shelf/Loop can never override the exact config image. */
  if (!isConfig && hv) {
    src = hv.src;
    const trayOn = state.trayColour && state.trayColour !== 'No Tray';
    if (trayOn) {
      const trayKey = ncoHandleize(state.trayColour);   /* full "Lilac Tray" -> "lilac-tray" */
      src = (hv.tray_srcs && hv.tray_srcs[trayKey]) || hv.src_tray || hv.src;
    }
  }
  if (!src) return;
  /* Alt text: a config image resolves even for a colour absent from colour_image_map
     (the Loop colour-gap), where hv.alt is empty — which previously left the alt on the
     prior colour (e.g. Loop Custard image with "Blush Pink" alt). Derive alt from the
     product title + selected colour for config images. Exact Batch 10 renders also name
     the selected overflow finish so the main and thumbnail alt describe the visible image. */
  const altParts = [];
  if (productTitleFallback) altParts.push(productTitleFallback);
  if (state.colour) altParts.push(state.colour);
  if (isOverflowFinish && state.overflowFinish) altParts.push(finishDisplayName(state.overflowFinish) + ' Overflow');
  const derivedAlt = altParts.join(' - ');
  const alt = isConfig ? (derivedAlt || (hv && hv.alt) || '') : ((hv && hv.alt) || derivedAlt || '');
  /* Request responsive derivatives for BOTH sources. The previous comment here claimed
     "colour_image_map srcs are already sized, so leave them" — that was wrong. Those URLs
     carry no width at all, so the PDP was downloading the full master TWICE, once for the
     main image and again for the thumbnail. Measured on a live cylinder image: 60,308 B
     unsized vs 34,418 B at width=1200 and 6,549 B at width=400. The Batch 11 tray matrix
     makes it worse still — 2048x2048 masters, a fresh one on every tray swap. withWidth
     is idempotent, so a URL that already carries a width is left at the size we want. */
  const mainSrc  = withWidth(src, 1200);
  const thumbSrc = withWidth(src, 400);
  const main = document.querySelector('[id^="merged-variant-img-"]');
  const thumb = document.querySelector('[id^="merged-variant-thumb-"]');
  if (main) { main.src = mainSrc; main.removeAttribute('srcset'); if (alt) main.alt = alt; }
  if (thumb) { thumb.src = thumbSrc; thumb.removeAttribute('srcset'); if (alt) thumb.alt = alt; }
}

/* Refresh the high-variant image from the CURRENT colour + tray selection,
   independent of whether the full option combination resolves to a purchasable
   variant. Retained as a defensive fallback: the cylinder grids are now COMPLETE
   (29 or 14 colours x 30 trays, every combination purchasable), so this branch
   should no longer be reachable on a cylinder. It previously read "cylinders have
   a partial colour x tray matrix (a Terra tray is not offered on a Signature
   basin)", which was reversed during the cylinder rebuild. This is a defensive fallback only: the main recovery path resets invalid
   tray selections to the real No Tray variant so price/SKU/cart state also stay
   correct. No-op for non-high-variant products and on initial load. */
function updateHvImageFromState() {
  if (initialLoad || !hvColourImg || !state.colour) return;
  const hv = hvColourImg[ncoHandleize(state.colour)];
  if (!hv || !hv.src) return;
  /* This runs only in applyResolved's UNAVAILABLE branch — no purchasable variant
     for the current selection, which for cylinders means the selected tray is not
     offered on this colour. Show the plain no-tray shot of the selected colour, not
     a representative tray shot that would imply that specific tray is available.
     Passing an hv with only src/alt makes updateHvImage fall through to src
     regardless of the (now-invalid) tray selection. Valid tray combos still render
     the representative tray via the success path (updateVariantId -> updateHvImage). */
  updateHvImage({ _hvImage: { src: hv.src, alt: hv.alt } });
}

function recoverInvalidHvTraySelection() {
  if (!hvIndex || !state.trayColour || state.trayColour === 'No Tray') return false;

  const noTrayBtn = rootEl.querySelector(
    '[data-nco-tray-colour-option][data-tray-colour-value="No Tray"]'
  );
  if (
    !noTrayBtn ||
    noTrayBtn.disabled ||
    noTrayBtn.hidden ||
    noTrayBtn.getAttribute('aria-disabled') === 'true'
  ) {
    return false;
  }

  const previousTray = state.trayColour;
  state.trayColour = noTrayBtn.getAttribute('data-tray-colour-value') || 'No Tray';
  updateOptionButtons(SEL.trayColourOption, 'data-tray-colour-value', state.trayColour);

  const label = rootEl.querySelector(SEL.trayColourLabel);
  if (label) label.textContent = state.trayColour;

  if (resolveVariant()) {
    applyResolved();
    return true;
  }

  state.trayColour = previousTray;
  updateOptionButtons(SEL.trayColourOption, 'data-tray-colour-value', state.trayColour);
  if (label) label.textContent = state.trayColour;
  return false;
}

function hasColourControlValue(colourName) {
  const swatches = rootEl ? rootEl.querySelectorAll(SEL.colourSwatch) : [];
  for (let i = 0; i < swatches.length; i++) {
    if (swatches[i].getAttribute('data-colour-name') === colourName) return true;
  }
  return false;
}

function resolveTrayURLParam(trayParam) {
  if (trayParam !== 'With Tray') return trayParam;

  const buttons = rootEl.querySelectorAll(SEL.trayColourOption);
  if (!buttons.length) return trayParam;

  const selectedColourHandle = ncoHandleize(state.colour);
  let firstTray = '';

  for (let i = 0; i < buttons.length; i++) {
    const value = buttons[i].getAttribute('data-tray-colour-value') || '';
    if (!value || value === 'No Tray') continue;
    if (!firstTray) firstTray = value;

    const trayBase = value.replace(/\s+Tray$/i, '');
    if (selectedColourHandle && ncoHandleize(trayBase) === selectedColourHandle) {
      return value;
    }
  }

  return firstTray || trayParam;
}

/* The Finish option was renamed — "Raw" -> "Raw Textured", "Signature" ->
   "Signature Smooth" — because the bare words collided with the retired
   Signature/Conscious/Terra colour ranges. These now name a surface texture only.

   The alias is two-way and availability-driven, not a one-way rewrite, because
   both spellings are live at once in two different senses: the homepage keeps
   linking to ?finish=Raw permanently (that copy was deliberately left alone),
   and during the Shopify migration one product can carry the new value while
   the next still carries the old. So: honour the requested value if a variant
   actually has it, otherwise try its counterpart.

   Returns the variant's own stored spelling, because button activation compares
   exactly (updateOptionButtons) — returning "raw textured" for a variant storing
   "Raw Textured" would resolve the variant but leave no button lit. */
const FINISH_ALIASES = {
  'raw': 'Raw Textured',
  'raw textured': 'Raw',
  'signature': 'Signature Smooth',
  'signature smooth': 'Signature',
};

function findStoredFinish(candidate) {
  if (!candidate || !variantStore || !variantStore.variants) return '';
  const needle = String(candidate).toLowerCase();
  for (let i = 0; i < variantStore.variants.length; i++) {
    const stored = variantStore.variants[i].finish || '';
    if (stored && String(stored).toLowerCase() === needle) return stored;
  }
  return '';
}

function resolveFinishURLParam(finishParam) {
  const direct = findStoredFinish(finishParam);
  if (direct) return direct;

  const counterpart = FINISH_ALIASES[String(finishParam || '').toLowerCase()];
  const aliased = findStoredFinish(counterpart);
  if (aliased) return aliased;

  /* Unknown on this product — pass through unchanged so resolveVariant fails
     the same way it always has, rather than silently selecting a wrong finish. */
  return finishParam;
}

function findVariantByColour(colourName) {
  if (!variantStore || !variantStore.variants) return null;
  const lower = colourName.toLowerCase();
  for (let i = 0; i < variantStore.variants.length; i++) {
    const vColour = (variantStore.variants[i].colourName || '').toLowerCase();
    if (vColour === lower) return variantStore.variants[i];
  }
  return null;
}

/* The client-side money formatter was removed with the market-price fix. Every price
   string now comes from Liquid's money_with_currency (presentment-market aware) or from
   the tier-3 section, so formatting a raw cents value against shop.money_format is never
   correct. variantStore.moneyFormat is consequently unused and can be dropped from
   snippets/nco-pdp-variant-controls.liquid on a later pass. */

function getURLParam(name) {
  const params = new URLSearchParams(window.location.search);
  return params.get(name) || '';
}

function escapeHTML(str) {
  const div = document.createElement('div');
  div.appendChild(document.createTextNode(str));
  return div.innerHTML;
}

/* ------------------------------------------------------------------ */
/* Variant resolution                                                  */
/* ------------------------------------------------------------------ */

function filterByField(arr, field, value) {
  const result = [];
  for (let i = 0; i < arr.length; i++) {
    const fieldVal = arr[i][field] || '';
    if (String(fieldVal).toLowerCase() === String(value).toLowerCase()) {
      result.push(arr[i]);
    }
  }
  return result;
}

function filterByTitle(arr, value) {
  const result = [];
  const needle = String(value || '').toLowerCase();
  for (let i = 0; i < arr.length; i++) {
    const title = String(arr[i].title || '').toLowerCase();
    if (title.indexOf(needle) !== -1) {
      result.push(arr[i]);
    }
  }
  return result;
}

/**
 * Find the best matching variant for current state. Colour is the primary
 * discriminator; mount, overflow, and tap hole narrow further.
 */
function resolveVariant() {
  if (hvIndex) return resolveVariantFromIndex();
  const variants = variantStore.variants;
  let candidates = [];

  /* Filter by colour first */
  for (let i = 0; i < variants.length; i++) {
    if (state.colour && variants[i].colourName !== state.colour) continue;
    if (state.metalFinish && variants[i].metalFinish !== state.metalFinish) continue;
    if (state.concreteColour && variants[i].concreteColour !== state.concreteColour) continue;
    candidates.push(variants[i]);
  }

  if (candidates.length === 0) {
    return null;
  }

  /* Narrow by mount */
  if (state.mount) {
    candidates = filterByTitle(candidates, state.mount);
    if (candidates.length === 0) return null;
  }

  /* Narrow by finish */
  if (state.finish) {
    candidates = filterByField(candidates, 'finish', state.finish);
    if (candidates.length === 0) return null;
  }

  /* Narrow by size */
  if (state.sizeLabel) {
    candidates = filterByField(candidates, 'sizeLabel', state.sizeLabel);
    if (candidates.length === 0) return null;
  }

  /* Narrow by tone mode */
  if (state.toneMode) {
    candidates = filterByField(candidates, 'toneMode', state.toneMode);
    if (candidates.length === 0) return null;
  }

  /* Narrow by secondary colour */
  if (state.secondaryColour) {
    candidates = filterByField(candidates, 'secondaryColourName', state.secondaryColour);
    if (candidates.length === 0) return null;
  }

  /* Narrow by tray colour */
  if (state.trayColour) {
    candidates = filterByField(candidates, 'trayColourName', state.trayColour);
    if (candidates.length === 0) return null;
  }

  /* Narrow by overflow */
  if (state.overflow) {
    candidates = filterByField(candidates, 'overflow', state.overflow);
    if (candidates.length === 0) return null;
  }

  /* Narrow by overflow finish */
  if (state.overflowFinish) {
    candidates = filterByField(candidates, 'overflowFinish', state.overflowFinish);
    if (candidates.length === 0) return null;
  }

  /* Narrow by tap hole */
  if (state.tapHole) {
    candidates = filterByField(candidates, 'tapHole', state.tapHole);
    if (candidates.length === 0) return null;
  }

  return candidates[0] || null;
}

function hasVariantForSelection(overrides) {
  const previous = { ...state };
  state.colour = Object.prototype.hasOwnProperty.call(overrides, 'colour') ? overrides.colour : state.colour;
  state.metalFinish = Object.prototype.hasOwnProperty.call(overrides, 'metalFinish') ? overrides.metalFinish : state.metalFinish;
  state.concreteColour = Object.prototype.hasOwnProperty.call(overrides, 'concreteColour') ? overrides.concreteColour : state.concreteColour;
  state.mount = Object.prototype.hasOwnProperty.call(overrides, 'mount') ? overrides.mount : state.mount;
  state.finish = Object.prototype.hasOwnProperty.call(overrides, 'finish') ? overrides.finish : state.finish;
  state.sizeLabel = Object.prototype.hasOwnProperty.call(overrides, 'sizeLabel') ? overrides.sizeLabel : state.sizeLabel;
  state.toneMode = Object.prototype.hasOwnProperty.call(overrides, 'toneMode') ? overrides.toneMode : state.toneMode;
  state.secondaryColour = Object.prototype.hasOwnProperty.call(overrides, 'secondaryColour') ? overrides.secondaryColour : state.secondaryColour;
  state.trayColour = Object.prototype.hasOwnProperty.call(overrides, 'trayColour') ? overrides.trayColour : state.trayColour;
  state.overflow = Object.prototype.hasOwnProperty.call(overrides, 'overflow') ? overrides.overflow : state.overflow;
  state.overflowFinish = Object.prototype.hasOwnProperty.call(overrides, 'overflowFinish') ? overrides.overflowFinish : state.overflowFinish;
  state.tapHole = Object.prototype.hasOwnProperty.call(overrides, 'tapHole') ? overrides.tapHole : state.tapHole;
  const match = resolveVariant();
  Object.assign(state, previous);
  return Boolean(match);
}

/* ------------------------------------------------------------------ */
/* Event emission                                                      */
/* ------------------------------------------------------------------ */

function emitVariantChange(variant) {
  document.dispatchEvent(
    new CustomEvent('nco:variant:change', {
      bubbles: true,
      detail: {
        variantId: variant.id,
        variant_id: variant.id,
        colourName: variant.colourName,
        price: variant.priceUnresolved ? null : variant.price,
        available: variant.available,
        /* Initial (load-time) seeding vs a real user selection. Consumers that swap
           the gallery image (or record an interaction signal) skip the initial one so
           the page stays on the hero carousel image until the customer chooses. */
        initial: initialLoad,
      },
    })
  );
}

/* ------------------------------------------------------------------ */
/* DOM updates                                                         */
/* ------------------------------------------------------------------ */

function updatePrice(variant) {
  const priceEl = document.querySelector('#product-price');
  const compareEl = document.querySelector('#product-compare-price');
  if (!priceEl) return;

  /* Fail closed on a high-variant price we could not resolve to the presentment market.
     Leaving the server-rendered (market-correct) price in place is strictly better than
     printing a shop-currency amount under the local currency symbol. */
  if (variant.priceUnresolved) {
    /* Retaining the previous number would misquote the newly selected variant, because
       Add to Cart is already pointing at the new variant id. Replace it visibly instead;
       the cart and checkout quote Shopify's own line-item price. */
    priceEl.setAttribute('data-nco-price-stale', 'true');
    priceEl.setAttribute('aria-live', 'polite');
    priceEl.setAttribute('aria-busy', 'true');
    priceEl.textContent = 'Updating price…';
    if (compareEl) { compareEl.textContent = ''; compareEl.style.display = 'none'; }
    if (variant.id) hvResolvePriceAsync(variant.id);
    return;
  }
  priceEl.removeAttribute('data-nco-price-stale');
  priceEl.removeAttribute('aria-busy');
  priceEl.textContent = variant.priceDisplay;

  if (compareEl) {
    if (variant.compareAtPriceDisplay) {
      compareEl.textContent = variant.compareAtPriceDisplay;
      compareEl.style.display = '';
    } else {
      compareEl.textContent = '';
      compareEl.style.display = 'none';
    }
  }
}

function updateSku(variant) {
  const els = document.querySelectorAll(SEL.sku);
  for (let i = 0; i < els.length; i++) {
    els[i].textContent = variant && variant.sku ? variant.sku : '';
  }
}

function updateAvailability(variant) {
  const statusEl = document.querySelector(SEL.availabilityStatus);
  const leadEl = document.querySelector(SEL.availabilityLead);

  if (statusEl) {
    statusEl.textContent = variant.availability || '';
    statusEl.style.display = variant.availability ? '' : 'none';
  }
  if (leadEl) {
    leadEl.textContent = variant.leadTime || '';
    leadEl.style.display = variant.leadTime ? '' : 'none';
  }
}

function setChipValue(container, chipType, value) {
  const chip = container.querySelector('[data-chip-type="' + chipType + '"]');
  if (!chip) return;
  const label = chip.querySelector(SEL.chipValue);
  if (label) {
    label.textContent = value;
  }
  chip.style.display = value ? '' : 'none';
}

function buildDimensions(variant) {
  if (!variant.widthMm) return '';
  let dim = String(variant.widthMm);
  if (variant.depthMm) dim += ' x ' + variant.depthMm;
  if (variant.heightMm) dim += ' x ' + variant.heightMm;
  return dim + 'mm';
}

function updateChips(variant) {
  const chipsEl = document.querySelector(SEL.chips);
  if (!chipsEl) return;

  setChipValue(chipsEl, 'dimensions', buildDimensions(variant));
  setChipValue(chipsEl, 'lead-time', variant.leadTime || '');
  setChipValue(chipsEl, 'overflow', variant.overflow || '');
  setChipValue(chipsEl, 'waste', variant.waste || '');
}

function updateVariantId(variant) {
  const inputs = document.querySelectorAll(SEL.variantIdInput);
  for (let i = 0; i < inputs.length; i++) {
    inputs[i].value = variant.id;
  }

  /* Also update any native Shopify variant selects */
  const nativeSelect = document.querySelector(
    'select[name="id"], input[name="id"]:not(' + SEL.variantIdInput + ')'
  );
  if (nativeSelect) {
    nativeSelect.value = variant.id;
  }

  /* Bridge to the merged-template gallery: product-image.liquid swaps the main
     image on a bubbling `change` of input[name="id"]. A programmatic `.value=`
     assignment does NOT fire `change`, so dispatch it here.
     Skip on initial load: the `.value=` above already seeds the add-to-cart id, but
     firing `change` would make product-image.liquid slickGoTo(0) onto the default
     colour image instead of leaving the hero carousel showing. */
  if (!initialLoad) {
    const idInputs = document.querySelectorAll('input[name="id"], select[name="id"]');
    for (let i = 0; i < idInputs.length; i++) {
      idInputs[i].dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* High-variant products: the capped gallery image map won't have this variant, so
     swap the image from the per-colour map directly. Also skipped on initial load. */
  if (variant && !initialLoad && (variant._hvImage || hvConfigurationImages)) updateHvImage(variant);
}

function loadColourContent(colourName) {
  const display = document.querySelector(SEL.colourContentDisplay);
  if (!display || !colourContentStore) return;

  /* Find content by colour name or handle (lowercased, hyphenated) */
  const handle = colourName.toLowerCase().replace(/\s+/g, '-');
  let content =
    colourContentStore[handle] || colourContentStore[colourName] || null;

  if (!content) {
    /* Try case-insensitive search */
    const keys = Object.keys(colourContentStore);
    for (let i = 0; i < keys.length; i++) {
      const entry = colourContentStore[keys[i]];
      if (
        entry.colourName &&
        entry.colourName.toLowerCase() === colourName.toLowerCase()
      ) {
        content = entry;
        break;
      }
    }
  }

  if (!content) {
    display.innerHTML = '';
    return;
  }

  let html = '';
  if (content.undertone) {
    html +=
      '<div class="nco-pdp-variants__colour-detail">' +
      '<span class="nco-pdp-variants__colour-detail-label">Undertone</span>' +
      '<span class="nco-pdp-variants__colour-detail-value">' +
      escapeHTML(content.undertone) +
      '</span></div>';
  }
  if (content.lightBehaviour) {
    html +=
      '<div class="nco-pdp-variants__colour-detail">' +
      '<span class="nco-pdp-variants__colour-detail-label">Light behaviour</span>' +
      '<span class="nco-pdp-variants__colour-detail-value">' +
      escapeHTML(content.lightBehaviour) +
      '</span></div>';
  }
  if (content.pairings) {
    html +=
      '<div class="nco-pdp-variants__colour-detail">' +
      '<span class="nco-pdp-variants__colour-detail-label">Pairs with</span>' +
      '<span class="nco-pdp-variants__colour-detail-value">' +
      escapeHTML(content.pairings) +
      '</span></div>';
  }
  if (content.styleMatch) {
    html +=
      '<div class="nco-pdp-variants__colour-detail">' +
      '<span class="nco-pdp-variants__colour-detail-label">Style match</span>' +
      '<span class="nco-pdp-variants__colour-detail-value">' +
      escapeHTML(content.styleMatch) +
      '</span></div>';
  }

  display.innerHTML = html;
}

function updateOptionButtons(selector, dataAttr, activeValue) {
  const buttons = rootEl.querySelectorAll(selector);
  for (let i = 0; i < buttons.length; i++) {
    const isMatch = buttons[i].getAttribute(dataAttr) === activeValue;
    /* Tray + overflow-finish render as dot swatches; everything else as text buttons.
       Toggle the active modifier that matches the button's base class so the correct
       visual state shows for each. */
    const activeClass = buttons[i].classList.contains('nco-pdp-variants__swatch')
      ? CSS.swatchActive
      : CSS.optionActive;
    buttons[i].classList.toggle(activeClass, isMatch);
    buttons[i].setAttribute('aria-checked', isMatch ? 'true' : 'false');
  }
}

/* Paint every dot ([data-nco-hexkey]) from the complete colour→hex map. Covers colour
   swatches, tray swatches, and overflow-finish swatches with one source of truth, so no
   dot can inherit a wrong colour from a capped metaobject lookup. */
function applySwatchHexes() {
  if (!colourHexMap) return;
  const dots = rootEl.querySelectorAll(SEL.swatchHexTarget);
  for (let i = 0; i < dots.length; i++) {
    const key = dots[i].getAttribute('data-nco-hexkey') || '';
    const hex = colourHexMap[key];
    if (hex) dots[i].style.backgroundColor = hex;
  }
}

function setButtonAvailability(button, available, keepVisible) {
  button.disabled = !available;
  button.setAttribute('aria-disabled', available ? 'false' : 'true');
  button.hidden = keepVisible ? false : !available;
}

/* Colour visibility is by EXISTENCE, not stock. These are made-to-order handmade
   products: every colour the product comes in must always be shown (and selectable)
   regardless of inventory quantity. The high-variant path was already existence-based;
   the standard path previously gated on `.available` (stock), which hid any 0-stock
   colour (e.g. Sage/Slate/Storm/Wheat on hoop-basin → 25 of 29 shown) and would hide
   the whole colour selector once stock is zeroed. Purchasability for out-of-stock
   variants is governed by the Shopify inventory policy, not by hiding the swatch. */
function hasAnyVariantForColour(colourName) {
  if (hvIndex && hvOptionNames) {
    let ci = hvOptionNames.indexOf('Colour');
    if (ci < 0) ci = 0;
    for (const k in hvIndex) { if (k.split('|')[ci] === colourName) return true; }
    return false;
  }
  for (let i = 0; i < variantStore.variants.length; i++) {
    if (variantStore.variants[i].colourName === colourName) {
      return true;
    }
  }
  return false;
}

function updateOptionAvailability() {
  const isAlchemy = String(rootEl.getAttribute('data-nco-product-handle') || '').indexOf('alchemy') === 0;
  const optionGroups = [
    [SEL.colourSwatch, 'data-colour-name', 'colour'],
    [SEL.alchemyOption + '[data-nco-alchemy-option="metalFinish"]', 'data-alchemy-option-value', 'metalFinish'],
    [SEL.alchemyOption + '[data-nco-alchemy-option="concreteColour"]', 'data-alchemy-option-value', 'concreteColour'],
    [SEL.mountOption, 'data-mount-value', 'mount'],
    [SEL.finishOption, 'data-finish-value', 'finish'],
    [SEL.sizeOption, 'data-size-value', 'sizeLabel'],
    [SEL.toneOption, 'data-tone-value', 'toneMode'],
    [SEL.secondaryColourOption, 'data-secondary-colour-value', 'secondaryColour'],
    [SEL.trayColourOption, 'data-tray-colour-value', 'trayColour'],
    [SEL.overflowOption, 'data-overflow-value', 'overflow'],
    [SEL.overflowFinishOption, 'data-overflow-finish-value', 'overflowFinish'],
    [SEL.tapholeOption, 'data-taphole-value', 'tapHole'],
  ];

  for (let i = 0; i < optionGroups.length; i++) {
    const [selector, attr, stateKey] = optionGroups[i];
    /* Index path: only colour and tray can be a partial matrix (cylinders are 702 of 29x30).
       The Configuration facets (tap hole / overflow state / overflow finish) and mount are
       full-matrix for shelf/loop, and the overflow<->finish coupling is managed by
       selectOverflow/selectOverflowFinish — greying them here would incorrectly hide a valid
       state (e.g. "Overflow" before a finish is chosen builds an incomplete config). Skip them. */
    if (hvIndex && stateKey !== 'colour' && stateKey !== 'trayColour') continue;
    const buttons = rootEl.querySelectorAll(selector);
    let visibleCount = 0;
    let lastVisibleButton = null;
    for (let j = 0; j < buttons.length; j++) {
      const value = buttons[j].getAttribute(attr) || '';
      let available;
      if (stateKey === 'colour') {
        available = hasAnyVariantForColour(value);
      } else if (stateKey === 'metalFinish' || stateKey === 'concreteColour') {
        /* Alchemy is a sparse matrix. Keep every value that participates in at least
           one valid pairing reachable; otherwise the currently-selected paired value
           can disable the very finish the shopper needs to click next. The selection
           handler then recovers the paired axis to the first valid combination and
           immediately repaints the image, SKU, price, and variant id. */
        const pairedKey = stateKey === 'metalFinish' ? 'concreteColour' : 'metalFinish';
        const probe = { [stateKey]: value, [pairedKey]: '' };
        /* Once a metal finish is selected, concrete colours must describe that
           finish only. Metal finishes remain broadly reachable so changing metal
           can still recover its paired concrete colour. */
        if (isAlchemy && stateKey === 'concreteColour' && state.metalFinish) {
          probe[pairedKey] = state.metalFinish;
        }
        available = hasVariantForSelection(probe);
      } else {
        available = hasVariantForSelection({ [stateKey]: value });
      }
      setButtonAvailability(buttons[j], available, isAlchemy && stateKey === 'concreteColour');
      if (available) {
        visibleCount++;
        lastVisibleButton = buttons[j];
      }
    }

    if (visibleCount === 1 && lastVisibleButton && !lastVisibleButton.classList.contains(CSS.optionActive)) {
      lastVisibleButton.click();
    }

    const group = buttons.length > 0 ? buttons[0].closest('[data-nco-variant-group]') : null;
    if (group) {
      group.hidden = visibleCount === 0;
    }
  }
}

function updateSpecSheet(variant) {
  if (!specSheetMap || !variant) return;
  /* Resolve the variant's registry handle (falling back to the product default for any
     variant beyond the 250-cap), then look up its URL in the uncapped registry map. */
  const handle =
    specSheetMap[String(variant.id)] ||
    specSheetMap[variant.id] ||
    specSheetMap.default;
  const url =
    handle && specRegistry && specRegistry[handle]
      ? String(specRegistry[handle])
      : '';
  const links = document.querySelectorAll(SEL.specSheetLink);
  const listItems = document.querySelectorAll(SEL.specSheetListItem);
  const messages = document.querySelectorAll(SEL.specSheetUnavailable);

  for (let i = 0; i < links.length; i++) {
    if (url) {
      links[i].setAttribute('href', url);
      links[i].hidden = false;
      links[i].removeAttribute('aria-hidden');
      links[i].removeAttribute('tabindex');
    } else {
      links[i].removeAttribute('href');
      links[i].hidden = true;
      links[i].setAttribute('aria-hidden', 'true');
      links[i].setAttribute('tabindex', '-1');
    }
  }

  for (let i = 0; i < messages.length; i++) {
    messages[i].hidden = Boolean(url);
    messages[i].textContent = url
      ? ''
      : 'Spec sheet unavailable for this variant';
  }

  for (let i = 0; i < listItems.length; i++) {
    listItems[i].hidden = !url;
  }
}

function updateInstallGuides() {
  // Bridge state: install guides stay static/shared — no mount-specific switching.
}

function formatNum(n, decimals) {
  const s = Number(n).toFixed(decimals);
  return s.replace(/\.?0+$/, '') || '0';
}

function updateSpecs(variant) {
  if (!variant) return;
  const weightEl = document.querySelector('[data-nco-spec-weight]');
  const dimEl = document.querySelector('[data-nco-spec-dimensions]');
  const capEl = document.querySelector('[data-nco-spec-capacity]');

  if (weightEl && variant.weightKg) {
    const kg = Number(variant.weightKg);
    const lbs = kg * 2.20462;
    weightEl.textContent = formatNum(kg, 1) + 'kg (' + formatNum(lbs, 2) + 'lbs)';
  }
  if (dimEl && variant.widthMm) {
    const w = Number(variant.widthMm);
    const h = Number(variant.heightMm);
    const wIn = w * 0.03937;
    const hIn = h * 0.03937;
    dimEl.textContent = 'D' + w + 'mm x H' + h + 'mm (D' + formatNum(wIn, 2) + 'in x H' + formatNum(hIn, 2) + 'in)';
  }
  if (capEl && variant.capacityL) {
    const litres = Number(variant.capacityL);
    const gal = litres * 0.264172;
    capEl.textContent = formatNum(litres, 1) + 'L (' + formatNum(gal, 2) + 'gal)';
  }
}

/* ------------------------------------------------------------------ */
/* Selection handlers                                                  */
/* ------------------------------------------------------------------ */

function clearInvalidExplicitVariant() {
  invalidExplicitVariant = false;
}

function selectColour(colourName, variantId) {
  clearInvalidExplicitVariant();
  state.colour = colourName;

  /* Update active swatch visual */
  const swatches = rootEl.querySelectorAll(SEL.colourSwatch);
  for (let i = 0; i < swatches.length; i++) {
    const isMatch =
      swatches[i].getAttribute('data-colour-name') === colourName;
    swatches[i].classList.toggle(CSS.swatchActive, isMatch);
    swatches[i].setAttribute('aria-checked', isMatch ? 'true' : 'false');
  }

  /* Update colour label */
  const label = rootEl.querySelector(SEL.colourLabel);
  if (label) {
    label.textContent = colourName;
  }

  const explicit = findVariantById(variantId);
  if (explicit && !state.finish) state.finish = explicit.finish || '';
  if (explicit && !state.sizeLabel) state.sizeLabel = explicit.sizeLabel || '';
  if (explicit && !state.toneMode) state.toneMode = explicit.toneMode || '';

  applyResolved();
  loadColourContent(colourName);
}

function selectAlchemyOption(optionKey, value) {
  clearInvalidExplicitVariant();
  if (optionKey !== 'metalFinish' && optionKey !== 'concreteColour') return;
  state[optionKey] = value;

  /* Alchemy is a sparse two-axis matrix rather than a full grid. If the new
     finish/colour cannot coexist with the other currently-selected axis,
     recover to the first valid variant on the new axis and repaint the paired
     selector. This prevents a dead-end where the clicked dot is rejected only
     because the previous paired value is not offered for it. */
  if (!resolveVariant()) {
    const pairedKey = optionKey === 'metalFinish' ? 'concreteColour' : 'metalFinish';
    state[pairedKey] = '';
    const recovered = resolveVariant();
    if (recovered) state[pairedKey] = recovered[pairedKey] || '';
  }

  const buttons = rootEl.querySelectorAll(SEL.alchemyOption);
  for (let i = 0; i < buttons.length; i++) {
    const buttonKey = buttons[i].getAttribute('data-nco-alchemy-option');
    if (buttonKey !== optionKey && buttonKey !== (optionKey === 'metalFinish' ? 'concreteColour' : 'metalFinish')) continue;
    const activeValue = state[buttonKey] || '';
    const active = buttons[i].getAttribute('data-alchemy-option-value') === activeValue;
    buttons[i].classList.toggle(CSS.swatchActive, active);
    buttons[i].setAttribute('aria-checked', active ? 'true' : 'false');
  }

  const labels = rootEl.querySelectorAll('[data-nco-alchemy-label]');
  for (let i = 0; i < labels.length; i++) {
    const labelKey = labels[i].getAttribute('data-nco-alchemy-label') || '';
    if (labelKey && state[labelKey]) labels[i].textContent = state[labelKey];
  }
  applyResolved();
}

/* Gallery selections are a second, equivalent way to choose an Alchemy
   configuration. Keep the controller as the single source of truth: seed the
   exact variant represented by the clicked gallery image, repaint both swatch
   axes, then let the normal resolver update price, availability, hidden id and
   all downstream PDP state. */
function handleGalleryVariant(e) {
  if (!rootEl || !variantStore) return;
  const detail = e.detail || {};
  const handle = rootEl.getAttribute('data-nco-product-handle') || '';
  if (!/^alchemy-/i.test(handle)) return;
  if (detail.productHandle && detail.productHandle !== handle) return;

  const variant = findVariantById(detail.variantId || detail.variant_id) ||
    findHighVariantById(detail.variantId || detail.variant_id);
  if (!variant) return;

  const currentId = rootEl.querySelector(SEL.variantIdInput);
  if (currentId && String(currentId.value) === String(variant.id)) return;

  clearInvalidExplicitVariant();
  seedStateFromVariant(variant);

  const alchemyButtons = rootEl.querySelectorAll(SEL.alchemyOption);
  for (let i = 0; i < alchemyButtons.length; i++) {
    const optionKey = alchemyButtons[i].getAttribute('data-nco-alchemy-option') || '';
    const active = optionKey && alchemyButtons[i].getAttribute('data-alchemy-option-value') === state[optionKey];
    alchemyButtons[i].classList.toggle(CSS.swatchActive, active);
    alchemyButtons[i].setAttribute('aria-checked', active ? 'true' : 'false');
  }

  const labels = rootEl.querySelectorAll('[data-nco-alchemy-label]');
  for (let i = 0; i < labels.length; i++) {
    const optionKey = labels[i].getAttribute('data-nco-alchemy-label') || '';
    if (optionKey && state[optionKey]) labels[i].textContent = state[optionKey];
  }

  applyResolved();
}

function selectMount(mountValue) {
  clearInvalidExplicitVariant();
  state.mount = mountValue;
  updateOptionButtons(SEL.mountOption, 'data-mount-value', mountValue);
  applyResolved();
}

function selectOverflow(overflowValue) {
  clearInvalidExplicitVariant();
  state.overflow = overflowValue;
  updateOptionButtons(
    SEL.overflowOption,
    'data-overflow-value',
    overflowValue
  );

  /* Overflow finish only applies when overflow is present. Hide + clear the
     finish dots for "No Overflow" so the two coupled selectors never resolve
     to an impossible combination (No Overflow + a finish). */
  const hasOverflow = overflowValue && overflowValue.toLowerCase().indexOf('no') !== 0;
  const ofGroup = rootEl.querySelector(SEL.overflowFinishGroup);
  if (ofGroup) {
    ofGroup.hidden = !hasOverflow;
  }
  if (!hasOverflow) {
    state.overflowFinish = '';
  } else if (!state.overflowFinish) {
    /* Overflow requires a finish to resolve a variant. Default to the first finish option
       so selecting "Overflow" immediately shows a valid finished variant rather than an
       incomplete (unresolvable) state. */
    const ofBtns = rootEl.querySelectorAll(SEL.overflowFinishOption);
    for (let k = 0; k < ofBtns.length; k++) {
      const fv = ofBtns[k].getAttribute('data-overflow-finish-value') || '';
      if (fv) {
        state.overflowFinish = fv;
        updateOptionButtons(SEL.overflowFinishOption, 'data-overflow-finish-value', fv);
        const ofLabelDef = rootEl.querySelector(SEL.overflowFinishLabel);
        if (ofLabelDef) ofLabelDef.textContent = finishDisplayName(fv);
        break;
      }
    }
  }

  applyResolved();
}

/* Display-only finish name: the overflow_finish SKU-name (e.g. "Brass") may be PRESENTED
   under a different label (e.g. "Tiger Bronze"). The mapping lives once in Liquid, on each
   button's data-overflow-finish-display; this reads it back so the selected-finish label
   matches. It never touches data-overflow-finish-value / state / SKU / variant resolution. */
function finishDisplayName(value) {
  if (!value) return value || '';
  if (value === 'Brass') return 'Tiger Bronze';
  if (rootEl) {
    const btns = rootEl.querySelectorAll(SEL.overflowFinishOption);
    for (let i = 0; i < btns.length; i++) {
      if (btns[i].getAttribute('data-overflow-finish-value') === value) {
        return btns[i].getAttribute('data-overflow-finish-display') || value;
      }
    }
  }
  return value;
}

/* Resolve old/new OBR terminology to the value actually rendered by the current product.
   This keeps saved Brass URLs working after the Shopify option-value migration and keeps
   the selector operational while Shopify/CDN data propagates. */
function overflowFinishDataValue(value) {
  if (!value || !rootEl) return value || '';
  const buttons = rootEl.querySelectorAll(SEL.overflowFinishOption);
  for (let i = 0; i < buttons.length; i++) {
    if (buttons[i].getAttribute('data-overflow-finish-value') === value) return value;
  }
  const alias = value === 'Brass' ? 'Tiger Bronze' : (value === 'Tiger Bronze' ? 'Brass' : '');
  if (alias) {
    for (let i = 0; i < buttons.length; i++) {
      if (buttons[i].getAttribute('data-overflow-finish-value') === alias) return alias;
    }
  }
  return value;
}

function selectOverflowFinish(overflowFinishValue) {
  clearInvalidExplicitVariant();
  overflowFinishValue = overflowFinishDataValue(overflowFinishValue);
  state.overflowFinish = overflowFinishValue;
  updateOptionButtons(
    SEL.overflowFinishOption,
    'data-overflow-finish-value',
    overflowFinishValue
  );

  const ofLabel = rootEl.querySelector(SEL.overflowFinishLabel);
  if (ofLabel) ofLabel.textContent = finishDisplayName(overflowFinishValue);

  /* Choosing a finish implies overflow is present. */
  if (overflowFinishValue) {
    const ovBtns = rootEl.querySelectorAll(SEL.overflowOption);
    for (let i = 0; i < ovBtns.length; i++) {
      const val = ovBtns[i].getAttribute('data-overflow-value') || '';
      if (val && val.toLowerCase().indexOf('no') !== 0) {
        state.overflow = val;
        updateOptionButtons(SEL.overflowOption, 'data-overflow-value', val);
        break;
      }
    }
  }
  applyResolved();
}

function selectFinish(finishValue) {
  clearInvalidExplicitVariant();
  state.finish = finishValue;
  updateOptionButtons(SEL.finishOption, 'data-finish-value', finishValue);
  applyResolved();
}

function selectSize(sizeValue) {
  clearInvalidExplicitVariant();
  state.sizeLabel = sizeValue;
  updateOptionButtons(SEL.sizeOption, 'data-size-value', sizeValue);
  applyResolved();
}

function selectTone(toneValue) {
  clearInvalidExplicitVariant();
  state.toneMode = toneValue;
  updateOptionButtons(SEL.toneOption, 'data-tone-value', toneValue);

  const scGroup = rootEl.querySelector(SEL.secondaryColourGroup);
  if (scGroup) {
    scGroup.hidden = toneValue !== 'Two Tone';
  }

  if (toneValue !== 'Two Tone') {
    state.secondaryColour = '';
  }

  applyResolved();
}

function selectSecondaryColour(colourValue) {
  clearInvalidExplicitVariant();
  state.secondaryColour = colourValue;
  updateOptionButtons(
    SEL.secondaryColourOption,
    'data-secondary-colour-value',
    colourValue
  );

  const label = rootEl.querySelector(SEL.secondaryColourLabel);
  if (label) label.textContent = colourValue;

  applyResolved();
}

function selectTrayColour(colourValue) {
  clearInvalidExplicitVariant();
  state.trayColour = colourValue;
  updateOptionButtons(
    SEL.trayColourOption,
    'data-tray-colour-value',
    colourValue
  );

  const label = rootEl.querySelector(SEL.trayColourLabel);
  if (label) label.textContent = colourValue;

  applyResolved();
}

function selectTapHole(tapHoleValue) {
  clearInvalidExplicitVariant();
  state.tapHole = tapHoleValue;
  updateOptionButtons(
    SEL.tapholeOption,
    'data-taphole-value',
    tapHoleValue
  );
  applyResolved();
}

function getVariantSize(v) {
  if (v.sizeLabel) return v.sizeLabel;
  if (v.title) {
    const parts = v.title.split(' / ');
    if (parts.length >= 2) return parts[1].trim();
  }
  return '';
}

function updateTitleAndDescription(variant) {
  const titleEl = document.querySelector(SEL.productTitle);
  const optionTitleEl = document.querySelector(SEL.variantOptionTitle);
  const descEl = document.querySelector(SEL.productDescription);

  if (optionTitleEl) {
    const optionTitle = variant && variant.title && variant.title !== 'Default Title'
      ? variant.title
      : '';
    optionTitleEl.textContent = optionTitle;
    optionTitleEl.hidden = !optionTitle;
  }

  if (titleEl) {
    let newTitle = (variant && variant.titleOverride) || '';
    if (!newTitle && variant) {
      const size = getVariantSize(variant);
      if (size && size.toLowerCase() !== 'standard') {
        newTitle = size + ' ' + productTitleFallback;
      }
    }
    if (!newTitle) newTitle = productTitleFallback;
    if (newTitle) titleEl.textContent = newTitle;
  }
  if (descEl) {
    const newDesc = (variant && variant.descriptionOverride) || productDescriptionFallback;
    if (newDesc) descEl.innerHTML = newDesc;
  }
}

function applyResolved() {
  hvSelectionEpoch++;
  const resolved = resolveVariant();
  const addBtn = document.querySelector(SEL.addToCartBtn);
  const unavailMsg = document.querySelector(SEL.unavailableMsg);

  if (invalidExplicitVariant) {
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
    }
    if (unavailMsg) {
      unavailMsg.hidden = false;
      unavailMsg.textContent = 'This product configuration is no longer available. Please choose another option.';
    }
    return;
  }

  if (!resolved || !resolved.available) {
    /* Cylinders have a partial colour x tray matrix. If the customer switches to a
       basin colour that cannot use the currently-selected tray, recover to the
       purchasable No Tray variant instead of leaving stale price/SKU/cart state. */
    if (!resolved && recoverInvalidHvTraySelection()) return;

    if (addBtn) {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
    }
    if (unavailMsg) {
      unavailMsg.hidden = false;
      unavailMsg.textContent = resolved
        ? 'This combination is currently unavailable.'
        : 'This combination is not available. Please adjust your selections.';
    }
    if (resolved) {
      updatePrice(resolved);
      updateSku(resolved);
      updateVariantId(resolved);
      updateSpecSheet(resolved);
      updateInstallGuides(resolved);
      updateTitleAndDescription(resolved);
      updateSpecs(resolved);
    } else {
      updateTitleAndDescription(null);
      /* No purchasable variant for this combo (e.g. a tray colour not offered on
         this basin). Still refresh the image so it shows the basin the customer
         just selected, instead of freezing on the previous colour's shot. */
      updateHvImageFromState();
    }
    updateOptionAvailability();
    return;
  }

  if (addBtn) {
    addBtn.disabled = false;
    addBtn.removeAttribute('aria-disabled');
  }
  if (unavailMsg) unavailMsg.hidden = true;

  emitVariantChange(resolved);
  updatePrice(resolved);
  updateSku(resolved);
  updateAvailability(resolved);
  updateChips(resolved);
  updateVariantId(resolved);
  updateSpecSheet(resolved);
  updateInstallGuides(resolved);
  updateTitleAndDescription(resolved);
  updateSpecs(resolved);
  updateOptionAvailability();
}

/* ------------------------------------------------------------------ */
/* Event binding (delegation per S2)                                   */
/* ------------------------------------------------------------------ */

function bindEvents() {
  document.addEventListener('nco:gallery:variant', handleGalleryVariant);

  rootEl.addEventListener('click', function handleRootClick(e) {
    const swatch = e.target.closest(SEL.colourSwatch);
    if (swatch) {
      e.preventDefault();
      if (swatch.disabled || swatch.getAttribute('aria-disabled') === 'true') return;
      const colourName = swatch.getAttribute('data-colour-name') || '';
      const vid = parseInt(swatch.getAttribute('data-variant-id'), 10);
      selectColour(colourName, vid);
      return;
    }

    const alchemyOption = e.target.closest(SEL.alchemyOption);
    if (alchemyOption) {
      e.preventDefault();
      if (alchemyOption.disabled || alchemyOption.getAttribute('aria-disabled') === 'true') return;
      selectAlchemyOption(
        alchemyOption.getAttribute('data-nco-alchemy-option') || '',
        alchemyOption.getAttribute('data-alchemy-option-value') || '',
      );
      return;
    }

    const mountBtn = e.target.closest(SEL.mountOption);
    if (mountBtn) {
      e.preventDefault();
      if (mountBtn.disabled || mountBtn.getAttribute('aria-disabled') === 'true') return;
      const mountVal = mountBtn.getAttribute('data-mount-value') || '';
      selectMount(mountVal);
      return;
    }

    const finishBtn = e.target.closest(SEL.finishOption);
    if (finishBtn) {
      e.preventDefault();
      if (finishBtn.disabled || finishBtn.getAttribute('aria-disabled') === 'true') return;
      selectFinish(finishBtn.getAttribute('data-finish-value') || '');
      return;
    }

    const sizeBtn = e.target.closest(SEL.sizeOption);
    if (sizeBtn) {
      e.preventDefault();
      if (sizeBtn.disabled || sizeBtn.getAttribute('aria-disabled') === 'true') return;
      selectSize(sizeBtn.getAttribute('data-size-value') || '');
      return;
    }

    const toneBtn = e.target.closest(SEL.toneOption);
    if (toneBtn) {
      e.preventDefault();
      if (toneBtn.disabled || toneBtn.getAttribute('aria-disabled') === 'true') return;
      selectTone(toneBtn.getAttribute('data-tone-value') || '');
      return;
    }

    const scBtn = e.target.closest(SEL.secondaryColourOption);
    if (scBtn) {
      e.preventDefault();
      if (scBtn.disabled || scBtn.getAttribute('aria-disabled') === 'true') return;
      selectSecondaryColour(scBtn.getAttribute('data-secondary-colour-value') || '');
      return;
    }

    const tcBtn = e.target.closest(SEL.trayColourOption);
    if (tcBtn) {
      e.preventDefault();
      if (tcBtn.disabled || tcBtn.getAttribute('aria-disabled') === 'true') return;
      selectTrayColour(tcBtn.getAttribute('data-tray-colour-value') || '');
      return;
    }

    const overflowBtn = e.target.closest(SEL.overflowOption);
    if (overflowBtn) {
      e.preventDefault();
      if (overflowBtn.disabled || overflowBtn.getAttribute('aria-disabled') === 'true') return;
      const ovVal = overflowBtn.getAttribute('data-overflow-value') || '';
      selectOverflow(ovVal);
      return;
    }

    const overflowFinishBtn = e.target.closest(SEL.overflowFinishOption);
    if (overflowFinishBtn) {
      e.preventDefault();
      if (overflowFinishBtn.disabled || overflowFinishBtn.getAttribute('aria-disabled') === 'true') return;
      const ofVal = overflowFinishBtn.getAttribute('data-overflow-finish-value') || '';
      selectOverflowFinish(ofVal);
      return;
    }

    const tapholeBtn = e.target.closest(SEL.tapholeOption);
    if (tapholeBtn) {
      e.preventDefault();
      if (tapholeBtn.disabled || tapholeBtn.getAttribute('aria-disabled') === 'true') return;
      const thVal = tapholeBtn.getAttribute('data-taphole-value') || '';
      selectTapHole(thVal);
      return;
    }
  });

  /* Keyboard: arrow keys within radio groups */
  rootEl.addEventListener('keydown', function handleKeydown(e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

    const btn = e.target.closest('button[role="radio"]');
    if (!btn) return;

    const group = btn.closest('[role="radiogroup"]');
    if (!group) return;

    const buttons = Array.prototype.slice.call(
      group.querySelectorAll('button[role="radio"]')
    );
    const idx = buttons.indexOf(btn);
    if (idx === -1) return;

    e.preventDefault();
    const next =
      e.key === 'ArrowRight'
        ? buttons[(idx + 1) % buttons.length]
        : buttons[(idx - 1 + buttons.length) % buttons.length];
    next.focus();
    next.click();
  });
}

/* ------------------------------------------------------------------ */
/* Init                                                                */
/* ------------------------------------------------------------------ */

function init() {
  rootEl = document.querySelector(SEL.root);
  if (!rootEl) return;

  variantStore = parseJSON(SEL.variantData);
  colourContentStore = parseJSON(SEL.colourContent);
  specSheetMap = parseJSON(SEL.specSheetMap);
  specRegistry = parseJSON(SEL.specRegistry);
  hvIndex = parseJSON(SEL.variantIndex);
  hvColourImg = parseJSON(SEL.colourImageMap);
  buildHvMarketPrices();
  hvConfigurationImages = parseJSON(SEL.configurationImageMap);
  hvOverflowImages1 = parseJSON(SEL.configurationOverflowImageMap1);
  hvOverflowImages2 = parseJSON(SEL.configurationOverflowImageMap2);
  colourHexMap = parseJSON(SEL.colourHexMap);
  const hvMeta = parseJSON(SEL.hvMeta);
  hvOptionNames = (hvMeta && hvMeta.optionNames) ? hvMeta.optionNames : null;
  hvConfigMap = (hvMeta && hvMeta.configMap) ? hvMeta.configMap : null;
  if (!variantStore || !variantStore.variants) return;

  productTitleFallback = variantStore.productTitle || '';
  productDescriptionFallback = variantStore.productDescription || '';

  /* Seed state from current variant, preferring the matrix default config:
     Standard size, Surface mount, Signature finish, No tap hole, No overflow,
     Single tone. Score same-colour variants and pick the most-default one. */
  let current = findVariantById(variantStore.currentVariantId);
  if (current) {
    const initColour = current.colourName || (current.title || '').split(' / ')[0];
    const defaultScore = function (v) {
      const s = (v.sizeLabel || getVariantSize(v) || '').toLowerCase();
      const t = (v.title || '').toLowerCase();
      const fin = (v.finish || '').toLowerCase();
      const th = (v.tapHole || '').toLowerCase();
      const ov = (v.overflow || '').toLowerCase();
      const tone = (v.toneMode || '').toLowerCase();
      let sc = 0;
      if (s === '' || s === 'standard') sc += 32;
      if (t.indexOf('wall') === -1) sc += 16;          /* prefer Surface */
      /* Exact set, not a prefix: "signature smooth" must score, but a future
         finish merely starting with "signature" must not silently inherit the
         default. Both spellings are listed so the PDP keeps defaulting to the
         smooth finish either side of the Shopify rename. */
      if (fin === '' || fin === 'signature' || fin === 'signature smooth') sc += 8;
      if (tone === '' || tone.indexOf('single') === 0 || tone.indexOf('one') === 0) sc += 4;
      if (th === '' || th.indexOf('no') === 0) sc += 2;
      if (ov === '' || ov.indexOf('no') === 0) sc += 1;
      return sc;
    };
    let best = current, bestScore = -1;
    for (let si = 0; si < variantStore.variants.length; si++) {
      const sv = variantStore.variants[si];
      if (sv.available === false) continue;
      const svColour = sv.colourName || (sv.title || '').split(' / ')[0];
      if (svColour !== initColour) continue;
      const sc = defaultScore(sv);
      if (sc > bestScore) { bestScore = sc; best = sv; }
    }
    current = best;
    state.colour = current.colourName || '';
    state.metalFinish = current.metalFinish || '';
    state.concreteColour = current.concreteColour || '';
    state.finish = current.finish || '';
    state.sizeLabel = current.sizeLabel || '';
    state.toneMode = current.toneMode || '';
    state.secondaryColour = current.secondaryColourName || '';
    state.trayColour = current.trayColourName || '';
    state.overflow = current.overflow || '';
    state.overflowFinish = current.overflowFinish || '';
    state.tapHole = current.tapHole || '';
  }

  /* Seed mount from first active mount button */
  const activeMount = rootEl.querySelector(
    SEL.mountOption + '.' + CSS.optionActive
  );
  if (activeMount) {
    state.mount = activeMount.getAttribute('data-mount-value') || '';
  }

  /* Default the tray to "No Tray" when that option exists (all cylinder colours have a
     No Tray variant). Read the literal from the button's data attribute so it matches the
     value the HV index is keyed by (option value), not the tray_colour_name metafield. */
  const noTrayBtn = rootEl.querySelector(
    '[data-nco-tray-colour-option][data-tray-colour-value="No Tray"]'
  );
  if (noTrayBtn) {
    state.trayColour = noTrayBtn.getAttribute('data-tray-colour-value');
    /* Keep the "Tray colour:" label in sync with the defaulted selection (the label is
       server-rendered from the default variant's tray metafield, which is a colour). */
    const trayLabel = rootEl.querySelector(SEL.trayColourLabel);
    if (trayLabel) trayLabel.textContent = state.trayColour;
  }

  /* Sync button active states with JS-seeded state */
  const alchemyButtons = rootEl.querySelectorAll(SEL.alchemyOption);
  for (let i = 0; i < alchemyButtons.length; i++) {
    const optionKey = alchemyButtons[i].getAttribute('data-nco-alchemy-option') || '';
    const active = optionKey && alchemyButtons[i].getAttribute('data-alchemy-option-value') === state[optionKey];
    alchemyButtons[i].classList.toggle(CSS.swatchActive, active);
    alchemyButtons[i].setAttribute('aria-checked', active ? 'true' : 'false');
  }
  const alchemyLabels = rootEl.querySelectorAll('[data-nco-alchemy-label]');
  for (let i = 0; i < alchemyLabels.length; i++) {
    const optionKey = alchemyLabels[i].getAttribute('data-nco-alchemy-label') || '';
    if (optionKey && state[optionKey]) alchemyLabels[i].textContent = state[optionKey];
  }
  updateOptionButtons(SEL.sizeOption, 'data-size-value', state.sizeLabel);
  updateOptionButtons(SEL.mountOption, 'data-mount-value', state.mount);
  updateOptionButtons(SEL.finishOption, 'data-finish-value', state.finish);
  updateOptionButtons(SEL.toneOption, 'data-tone-value', state.toneMode);
  updateOptionButtons(SEL.trayColourOption, 'data-tray-colour-value', state.trayColour);
  updateOptionButtons(SEL.overflowOption, 'data-overflow-value', state.overflow);
  updateOptionButtons(SEL.overflowFinishOption, 'data-overflow-finish-value', state.overflowFinish);
  updateOptionButtons(SEL.tapholeOption, 'data-taphole-value', state.tapHole);

  /* Overflow finish only applies when overflow is present: hide on load if not */
  const initOfGroup = rootEl.querySelector(SEL.overflowFinishGroup);
  if (initOfGroup) {
    const initHasOverflow = state.overflow && state.overflow.toLowerCase().indexOf('no') !== 0;
    initOfGroup.hidden = !initHasOverflow;
  }

  /* Sync colour swatch active state */
  const swatches = rootEl.querySelectorAll(SEL.colourSwatch);
  for (let i = 0; i < swatches.length; i++) {
    const isMatch = swatches[i].getAttribute('data-colour-name') === state.colour;
    swatches[i].classList.toggle(CSS.swatchActive, isMatch);
    swatches[i].setAttribute('aria-checked', isMatch ? 'true' : 'false');
  }
  const colourLabel = rootEl.querySelector(SEL.colourLabel);
  if (colourLabel && state.colour) colourLabel.textContent = state.colour;

  /* Explicit ?variant= is authoritative. Shopify's normal variantStore is capped at 250 for
     high-variant products, so validate against the native array first and then the uncapped
     variant_index. A bad id is held in a safe unavailable state rather than silently falling
     back to the first purchasable configuration. */
  const urlVariant = getURLParam('variant');
  let explicitVariant = null;
  if (urlVariant) {
    explicitVariant = findVariantById(urlVariant) || findHighVariantById(urlVariant);
    if (explicitVariant) {
      seedStateFromVariant(explicitVariant);
    } else {
      invalidExplicitVariant = true;
    }
  }

  if (!explicitVariant && !invalidExplicitVariant) {
    /* Handle URL colour param */
    const urlColour = getURLParam('colour');
    if (urlColour) {
      const matchByURL = findVariantByColour(urlColour);
      if (matchByURL) {
        selectColour(matchByURL.colourName, matchByURL.id);
      } else if (hvIndex && hasColourControlValue(urlColour)) {
        /* High-variant products can have the requested colour beyond Liquid's
           250-variant cap. In that case, seed the colour directly and let
           variant_index resolve the complete option combination below. */
        selectColour(urlColour, null);
      }
    }

    /* Handle URL option params from legacy collection filter cards. */
    const urlMount = getURLParam('mount');
    if (urlMount && urlMount !== 'Freestanding') selectMount(urlMount);

    const urlSize = getURLParam('size');
    if (urlSize) selectSize(urlSize);

    const urlFinish = getURLParam('finish');
    if (urlFinish) selectFinish(resolveFinishURLParam(urlFinish));

    const urlOverflow = getURLParam('overflow');
    if (urlOverflow) selectOverflow(urlOverflow);

    const urlTapHole = getURLParam('tapHole');
    if (urlTapHole) {
      let thVal = urlTapHole;
      if (thVal === '1 Tap Hole' || thVal === 'Single Tap Hole') thVal = 'Yes';
      else if (thVal === 'No Tap Hole') thVal = 'No';
      selectTapHole(thVal);
    }

    const urlOverflowFinish = getURLParam('overflowFinish');
    if (urlOverflowFinish) selectOverflowFinish(urlOverflowFinish);

    const urlTray = getURLParam('tray');
    if (urlTray) selectTrayColour(resolveTrayURLParam(urlTray));
  }

  /* A valid explicit variant was seeded in one pass above. Repaint every control from that
     state after the legacy/default initialisation has finished, without firing intermediate
     resolutions that could briefly select a different configuration. */
  updateOptionButtons(SEL.sizeOption, 'data-size-value', state.sizeLabel);
  updateOptionButtons(SEL.mountOption, 'data-mount-value', state.mount);
  updateOptionButtons(SEL.finishOption, 'data-finish-value', state.finish);
  updateOptionButtons(SEL.toneOption, 'data-tone-value', state.toneMode);
  updateOptionButtons(SEL.trayColourOption, 'data-tray-colour-value', state.trayColour);
  updateOptionButtons(SEL.overflowOption, 'data-overflow-value', state.overflow);
  updateOptionButtons(SEL.overflowFinishOption, 'data-overflow-finish-value', state.overflowFinish);
  updateOptionButtons(SEL.tapholeOption, 'data-taphole-value', state.tapHole);
  const seededAlchemyButtons = rootEl.querySelectorAll(SEL.alchemyOption);
  for (let i = 0; i < seededAlchemyButtons.length; i++) {
    const optionKey = seededAlchemyButtons[i].getAttribute('data-nco-alchemy-option') || '';
    const active = optionKey && seededAlchemyButtons[i].getAttribute('data-alchemy-option-value') === state[optionKey];
    seededAlchemyButtons[i].classList.toggle(CSS.swatchActive, active);
    seededAlchemyButtons[i].setAttribute('aria-checked', active ? 'true' : 'false');
  }
  const seededAlchemyLabels = rootEl.querySelectorAll('[data-nco-alchemy-label]');
  for (let i = 0; i < seededAlchemyLabels.length; i++) {
    const optionKey = seededAlchemyLabels[i].getAttribute('data-nco-alchemy-label') || '';
    if (optionKey && state[optionKey]) seededAlchemyLabels[i].textContent = state[optionKey];
  }
  const seededSwatches = rootEl.querySelectorAll(SEL.colourSwatch);
  for (let i = 0; i < seededSwatches.length; i++) {
    const isMatch = seededSwatches[i].getAttribute('data-colour-name') === state.colour;
    seededSwatches[i].classList.toggle(CSS.swatchActive, isMatch);
    seededSwatches[i].setAttribute('aria-checked', isMatch ? 'true' : 'false');
  }
  const seededColourLabel = rootEl.querySelector(SEL.colourLabel);
  if (seededColourLabel && state.colour) seededColourLabel.textContent = state.colour;

  bindEvents();
  applySwatchHexes();
  updateOptionAvailability();
  applyResolved();

  /* Initial seeding complete. From here, selections swap the gallery image normally. */
  initialLoad = false;

  /* First settled paint for EXPLICIT entry (deep ?variant= or collection-filter params):
     updateHvImage is suppressed during init (initialLoad guard). Now that init is
     complete, apply the exact configuration image once so a deep-linked configuration is
     correct on the FIRST paint, not only after the first interaction. Handle-only entry
     (no params) stays hero-first. */
  const explicitIntent = !!(getURLParam('variant') || getURLParam('colour') || getURLParam('mount') ||
    getURLParam('overflow') || getURLParam('tapHole') || getURLParam('overflowFinish') ||
    getURLParam('finish') || getURLParam('size') || getURLParam('tray'));
  if (explicitIntent && (hvConfigurationImages || hvColourImg)) {
    const rv = resolveVariant();
    if (rv && (rv._hvImage || hvConfigurationImages)) updateHvImage(rv);
  }
}

/* ------------------------------------------------------------------ */
/* Bootstrap                                                           */
/* ------------------------------------------------------------------ */

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
